import { QuantityAdjustment } from '../../graphQL/queries/getSession/types'
import { IRoundingRules } from '../../interfaces'
import addPromotionalQtyToValue from './addPromotionalQtyToValue'

export interface ICalculateNewQuantity {
  originalValue: number
  minQtyAllowed: number
  promotionalQty: number
  inRangeValue: number
  autoAdd: boolean
  freegoodsAsSubitem: boolean
  jumpQty: number | string
  resultFixed: boolean
  resultValue: number
  minQty: number | null
  productQuantity: number
  nextValidValue?: number
  prevValidValue?: number
  quantityAdjustment?: `${QuantityAdjustment}`
  ruleInvalidatedBy?: IRoundingRules | string
  allowTypedZero?: boolean
  isValueOutOfRange?: boolean
}

interface IConditionsAdjustmentProps extends Pick<ICalculateNewQuantity, 'nextValidValue' | 'prevValidValue' | 'quantityAdjustment' | 'resultValue' | 'productQuantity'> {
  ruleInvalidatedBy?: IRoundingRules
}

interface IConditionsAdjustment {
  condition: (args: IConditionsAdjustmentProps) => boolean
  action: (args: IConditionsAdjustmentProps) => number
}

const conditionsAdjustment: IConditionsAdjustment[] = [
  {
    // If the quantityAdjustment mode is VALIDATE and there is a rule invalidation object,
    // we return the product's current quantity (no automatic adjustment),
    // just as a validation check.
    condition: ({ quantityAdjustment, ruleInvalidatedBy }: IConditionsAdjustmentProps) =>
      quantityAdjustment === QuantityAdjustment.VALIDATE && !!ruleInvalidatedBy,
    action: (args: IConditionsAdjustmentProps) => args.productQuantity
  },
  {
    // If mode is ROUND, and a rule invalidation exists, and the rule's "value"
    // is greater than the resulting quantity, use the next valid higher quantity.
    // (E.g., round up to the next allowed quantity value.)
    condition: ({ quantityAdjustment, ruleInvalidatedBy, resultValue }: IConditionsAdjustmentProps) =>
      quantityAdjustment === QuantityAdjustment.ROUND && !!ruleInvalidatedBy && ruleInvalidatedBy.value > resultValue,
    action: ({ nextValidValue = 0 }: IConditionsAdjustmentProps) => nextValidValue
  },
  {
    // If mode is ROUND, and a rule invalidation exists, and rule's "value"
    // is less than the resulting quantity, use the previous valid (lower) value.
    // (E.g., round down to the previous allowed value.)
    condition: ({ quantityAdjustment, ruleInvalidatedBy, resultValue }: IConditionsAdjustmentProps) =>
      quantityAdjustment === QuantityAdjustment.ROUND && !!ruleInvalidatedBy && ruleInvalidatedBy.value < resultValue,
    action: ({ prevValidValue = 0 }: IConditionsAdjustmentProps) => prevValidValue
  },
]

const isRuleInvalidatedBy = (maybeRuleInvalidatedBy: unknown): maybeRuleInvalidatedBy is IRoundingRules => {
  return typeof maybeRuleInvalidatedBy === 'object' && maybeRuleInvalidatedBy !== null && 'value' in maybeRuleInvalidatedBy && 'thresholdUp' in maybeRuleInvalidatedBy && 'thresholdDown' in maybeRuleInvalidatedBy
}

/**
 * Calculates the appropriate (validated and/or adjusted) product quantity based on various
 * cart constraints, promotional rules, and session-level configuration/logics.
 *
 * This function integrates a layered approach:
 *   1. Handles fixed results (when result should not be changed)
 *   2. Handles an explicit typed zero as a request to empty the line (opt-in)
 *   3. Handles adjustment-specific logic based on cart mode and invalidation rules
 *   4. Handles minimum quantity requirements
 *   5. Handles jump quantity and promotional quantity calculations
 *   6. Falls back to the given input value when no adjustment applies
 *
 * @param args {ICalculateNewQuantity} - An object containing all relevant configuration & values:
 *   - originalValue: The value input by the user or prior state.
 *   - minQtyAllowed: The minimal quantity allowed by configuration/rules.
 *   - inRangeValue: The value clamped inside allowed quantity range.
 *   - jumpQty: The quantity interval (e.g. must buy integer multiples of this).
 *   - autoAdd: If true, auto-add free/promotional goods logic applies.
 *   - freegoodsAsSubitem: If true, promotional qty is handled as sub-item logic.
 *   - promotionalQty: The quantity from active promotion.
 *   - resultFixed: If true, forcibly returns the provided resultValue.
 *   - resultValue: A fixed or "next" value suggested by a rounding/policy rule.
 *   - minQty: Optional absolute minimum quantity for this product.
 *   - productQuantity: Existing quantity of the product in the cart.
 *   - quantityAdjustment: Adjustment type per session/config (QuantityAdjustment enum).
 *   - ruleInvalidatedBy: If set, describes an invalidated rule with round/correction context.
 *   - nextValidValue: Used for rounding up in adjustment scenarios.
 *   - prevValidValue: Used for rounding down in adjustment scenarios.
 *   - allowTypedZero: If true, an explicit typed 0 resolves to 0 instead of being
 *     re-adjusted upwards by the adjustment/minQty/jumpQty rules below.
 *   - isValueOutOfRange: The counter's verdict on the typed value; false means it was
 *     accepted as-is. Used to tell a deliberate 0 from an abandoned edit.
 *
 * @returns {number} - The appropriate quantity value after applying all relevant cart and session rules.
 */
export const recalculateValueFromInput = (args: ICalculateNewQuantity): number => {
  const {
    originalValue,
    minQtyAllowed,
    inRangeValue,
    jumpQty,
    autoAdd,
    freegoodsAsSubitem,
    promotionalQty,
    resultFixed,
    resultValue,
    minQty,
    ruleInvalidatedBy: maybeRuleInvalidatedBy,
    productQuantity,
    quantityAdjustment,
    nextValidValue,
    prevValidValue,
    allowTypedZero,
    isValueOutOfRange,
  } = args

  const ruleInvalidatedBy = isRuleInvalidatedBy(maybeRuleInvalidatedBy) ? maybeRuleInvalidatedBy : undefined

  // 1. Forced/fixed result: return immediately
  if (resultFixed) {
    return resultValue
  }

  // 2. An explicit typed zero means "I want none of this", so return 0 — which the
  // caller turns into a removal — rather than letting the session adjustment, minQty
  // or jumpQty rules below pull it back to a non-zero quantity. Without this, the same
  // gesture resolves several different ways depending on the product's config. Note
  // this deliberately takes precedence over quantityAdjustment VALIDATE/ROUND too.
  //
  // Both operands matter. `originalValue` is the raw typed number, but a field holding
  // no parseable number coerces to 0 as well, so it alone cannot tell a deliberate zero
  // from an abandoned edit. `isValueOutOfRange` is the counter's own verdict on the
  // value: false means it accepted this as a real 0. Checking the verdict rather than
  // comparing `inRangeValue` to 0 matters when the line's current quantity is already
  // 0, because then a rejected value also reverts to 0 and would look accepted.
  if (allowTypedZero && originalValue === 0 && !isValueOutOfRange) {
    return 0
  }

  // 3. Check for adjustment logic based on cart/session/invalidated round rules
  const argsAdjustment = {
    nextValidValue,
    prevValidValue,
    quantityAdjustment,
    ruleInvalidatedBy,
    productQuantity,
    resultValue,
  }

  const conditionAdjustment = conditionsAdjustment.find((condition) => condition.condition(argsAdjustment))

  if (conditionAdjustment) {
    return conditionAdjustment.action(argsAdjustment)
  }

  // 4. Handle explicit minQty requirement if higher than previous and applies
  if (
    originalValue !== minQtyAllowed &&
    typeof minQty === 'number' &&
    minQty > originalValue
  ) {
    return minQty
  }

  // 5. JumpQty logic (for bulk/step purchases)
  if (typeof jumpQty === 'number' && jumpQty > 1) {
    const updatedOriginalValue = addPromotionalQtyToValue({
      valueToUpdate: originalValue,
      promotionalQty,
      freegoodsAsSubitem,
      autoAdd,
    })

    const quantityWithoutMin = updatedOriginalValue - minQtyAllowed
    const module = quantityWithoutMin % jumpQty
    const adjustedQuantity =
      module > 0 ? updatedOriginalValue - module : updatedOriginalValue
    const finalAdjustedQuantity =
      adjustedQuantity < minQtyAllowed ? minQtyAllowed : adjustedQuantity

    return finalAdjustedQuantity
  }

  // 6. Fallback: return the calculated/clamped in-range value
  return inRangeValue
}

export default recalculateValueFromInput
